import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Plus, X } from 'lucide-react';
import { toast } from 'sonner';
import logger from '../utils/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const TYPE_LABELS = { exchange: 'Exchange', currency: 'Currency', fund_type: 'Fund Type' };
const TYPE_ADD_LABELS = { exchange: 'Stock Exchange', currency: 'Currency', fund_type: 'Mutual Fund Type' };
const TYPE_ID_FIELDS = { exchange: 'exchange_id', currency: 'currency_id', fund_type: 'fund_type_id' };
const TYPE_CODE_PH = { exchange: 'e.g. ASX', currency: 'e.g. XYZ', fund_type: 'e.g. real_estate' };
const TYPE_NAME_PH = { exchange: 'e.g. Australian Securities Exchange', currency: 'e.g. Custom Currency', fund_type: 'e.g. Real Estate Fund' };
const TYPE_ENDPOINTS = { exchange: '/api/custom-exchanges', currency: '/api/custom-currencies', fund_type: '/api/custom-mutual-fund-types' };
const buildPayload = (type, item) => type === 'exchange' ? { code: item.code, name: item.name, country: item.country } : type === 'currency' ? { code: item.code, name: item.name, symbol: item.symbol } : { code: item.code, name: item.name, description: item.description };
const authHeaders = () => ({ 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` });

const EditableDropdown = ({ value, onValueChange, options, onOptionsUpdate, placeholder, type, className, disabled, dataTestId }) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [newItem, setNewItem] = useState({ code: '', name: '', country: '', symbol: '', description: '' });
  const [isLoading, setIsLoading] = useState(false);

  const handleAddItem = async () => {
    if (!newItem.code || !newItem.name) { toast.error('Please fill in all required fields'); return; }
    if (type === 'exchange' && !newItem.country) { toast.error('Country is required for exchanges'); return; }
    setIsLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}${TYPE_ENDPOINTS[type]}`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(buildPayload(type, newItem)) });
      if (!res.ok) { const error = await res.json(); throw new Error(error.detail || `Failed to add ${type}`); }
      const result = await res.json();
      toast.success(`${TYPE_LABELS[type]} added successfully!`);
      let label = result.name;
      if (type === 'exchange') label = `${result.name} - ${result.country}`;
      else if (type === 'currency' && result.symbol) label = `${result.name} (${result.symbol})`;
      else if (type === 'fund_type' && result.description) label = `${result.name} - ${result.description}`;
      onOptionsUpdate([...options, { value: result.code, label, is_system: false, [TYPE_ID_FIELDS[type]]: result[TYPE_ID_FIELDS[type]] }]);
      setNewItem({ code: '', name: '', country: '', symbol: '', description: '' }); setShowAddModal(false);
    } catch (error) { logger.error(`Error adding ${type}:`, error); toast.error(error.message); }
    finally { setIsLoading(false); }
  };

  const handleDeleteItem = async (option) => {
    if (option.is_system) { toast.error('Cannot delete system entries'); return; }
    if (!window.confirm(`Are you sure you want to delete "${option.label}"?`)) return;
    try {
      const res = await fetch(`${BACKEND_URL}${TYPE_ENDPOINTS[type]}/${option[TYPE_ID_FIELDS[type]]}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` } });
      if (!res.ok) { const error = await res.json(); throw new Error(error.detail || `Failed to delete ${type}`); }
      toast.success(`${TYPE_LABELS[type]} deleted successfully!`);
      onOptionsUpdate(options.filter(opt => opt.value !== option.value));
      if (value === option.value) onValueChange('');
    } catch (error) { logger.error(`Error deleting ${type}:`, error); toast.error(error.message); }
  };

  return (
    <div className="flex items-center space-x-2">
      <div className="flex-1">
        <Select value={value} onValueChange={onValueChange} disabled={disabled}>
          <SelectTrigger data-testid={dataTestId} className={className}><SelectValue placeholder={placeholder} /></SelectTrigger>
          <SelectContent>
            {options.map(option => (
              <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <Button type="button" variant="outline" size="sm" data-testid={`add-${type}-btn`} onClick={() => setShowAddModal(true)} className="shrink-0" disabled={disabled}><Plus className="h-4 w-4" /></Button>
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New {TYPE_ADD_LABELS[type]}</DialogTitle>
            <DialogDescription>Create a custom {TYPE_LABELS[type].toLowerCase()} entry available across your investments.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label htmlFor="code">Code *</Label><Input id="code" data-testid={`new-${type}-code`} placeholder={TYPE_CODE_PH[type]} value={newItem.code} onChange={(e) => setNewItem({ ...newItem, code: type === 'fund_type' ? e.target.value.toLowerCase().replace(/\s+/g, '_') : e.target.value.toUpperCase() })} maxLength={type === 'fund_type' ? 50 : 10} /></div>
            <div className="space-y-2"><Label htmlFor="name">Name *</Label><Input id="name" data-testid={`new-${type}-name`} placeholder={TYPE_NAME_PH[type]} value={newItem.name} onChange={(e) => setNewItem({ ...newItem, name: e.target.value })} /></div>
            {type === 'exchange' && <div className="space-y-2"><Label htmlFor="country">Country *</Label><Input id="country" data-testid="new-exchange-country" placeholder="e.g. Australia" value={newItem.country} onChange={(e) => setNewItem({ ...newItem, country: e.target.value })} /></div>}
            {type === 'currency' && <div className="space-y-2"><Label htmlFor="symbol">Symbol (Optional)</Label><Input id="symbol" data-testid="new-currency-symbol" placeholder="e.g. $" value={newItem.symbol} onChange={(e) => setNewItem({ ...newItem, symbol: e.target.value })} maxLength={5} /></div>}
            {type === 'fund_type' && <div className="space-y-2"><Label htmlFor="description">Description (Optional)</Label><Input id="description" data-testid="new-fund-type-description" placeholder="e.g. Focuses on real estate" value={newItem.description} onChange={(e) => setNewItem({ ...newItem, description: e.target.value })} /></div>}
          </div>
          <div className="flex justify-end space-x-2 pt-4">
            <Button variant="outline" onClick={() => setShowAddModal(false)} disabled={isLoading}>Cancel</Button>
            <Button onClick={handleAddItem} disabled={isLoading} data-testid={`confirm-add-${type}`}>{isLoading ? 'Adding...' : `Add ${TYPE_LABELS[type]}`}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EditableDropdown;
